import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { SITE_CONFIG, SERVICES_FR, SERVICES_EN, CITIES } from '@/data/siteData';
import { Send, Phone, MessageCircle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const { lang, t } = useLanguage();
  const services = lang === 'en' ? SERVICES_EN : SERVICES_FR;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    city: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        language: lang,
      });
      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        city: '',
        message: '',
      });
    } catch (err) {
      setError(t(
        "Une erreur s'est produite. Veuillez réessayer ou nous contacter par téléphone.",
        'An error occurred. Please try again or contact us by phone.'
      ));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="contact" className="py-20 bg-[#1a365d]" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-12 text-center shadow-2xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('Merci pour votre message !', 'Thank you for your message!')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t(
                'Nous vous répondrons dans les plus brefs délais.',
                'We will get back to you as soon as possible.'
              )}
            </p>
            <button
              onClick={() => setIsSuccess(false)}
              className="bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#C9A227] hover:to-[#B8960C] text-white px-6 py-3 rounded-full font-medium transition-all shadow-md"
            >
              {t('Envoyer un autre message', 'Send another message')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-[#1a365d]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Info */}
          <div className="text-white">
            <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4">
              {t('Contactez-nous', 'Contact Us')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('Prêt à Commencer ?', 'Ready to Get Started?')}
            </h2>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              {t(
                'Recevez votre estimation personnalisée en quelques minutes. Notre équipe vous répondra rapidement.',
                'Receive your personalized estimate in minutes. Our team will get back to you quickly.'
              )}
            </p>

            {/* Contact Options */}
            <div className="space-y-4">
              <a
                href={SITE_CONFIG.phoneLink}
                className="flex items-center gap-4 bg-white/10 hover:bg-white/15 rounded-xl p-4 transition-colors border border-white/10"
                data-testid="contact-phone-link"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#C9A227] rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">{t('Appelez-nous', 'Call us')}</p>
                  <p className="font-semibold text-lg">{SITE_CONFIG.phone}</p>
                </div>
              </a>

              <a
                href={SITE_CONFIG.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-white/10 hover:bg-white/15 rounded-xl p-4 transition-colors border border-white/10"
                data-testid="contact-whatsapp-link"
              >
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">WhatsApp</p>
                  <p className="font-semibold text-lg">{t('Discutez avec nous', 'Chat with us')}</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <h3 className="text-xl font-bold text-[#1a365d] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('Obtenir mon estimation gratuite', 'Get my free estimate')}
            </h3>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#1a365d] mb-1">
                  {t('Nom complet', 'Full name')} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all"
                  placeholder={t('Votre nom', 'Your name')}
                  data-testid="contact-form-name"
                />
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#1a365d] mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all"
                    placeholder="email@exemple.com"
                    data-testid="contact-form-email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#1a365d] mb-1">
                    {t('Téléphone', 'Phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all"
                    placeholder="+33 6 00 00 00 00"
                    data-testid="contact-form-phone"
                  />
                </div>
              </div>

              {/* Service & City */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-[#1a365d] mb-1">
                    {t('Service souhaité', 'Desired service')}
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white"
                    data-testid="contact-form-service"
                  >
                    <option value="">{t('Sélectionnez', 'Select')}</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-[#1a365d] mb-1">
                    {t('Ville', 'City')}
                  </label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all bg-white"
                    data-testid="contact-form-city"
                  >
                    <option value="">{t('Sélectionnez', 'Select')}</option>
                    {CITIES.map((city) => (
                      <option key={city.id} value={city.id}>
                        {lang === 'en' ? city.nameEn : city.nameFr}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#1a365d] mb-1">
                  {t('Message', 'Message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none transition-all resize-none"
                  placeholder={t('Décrivez votre besoin...', 'Describe your need...')}
                  data-testid="contact-form-message"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#C9A227] hover:to-[#B8960C] disabled:from-[#D4AF37]/70 disabled:to-[#C9A227]/70 text-white py-4 rounded-full font-semibold text-lg transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                data-testid="contact-form-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('Envoi en cours...', 'Sending...')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t('Envoyer ma demande', 'Send my request')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
